import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Alert,
  useTheme,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { getCheckOutBookings, getBookingDetails } from '../services/api';
import BookingDetailsDrawer from '../components/booking/BookingDetailsDrawer';
import BookingOpsBookingCard from '../components/booking/BookingOpsBookingCard';
import CheckOutDrawer from '../components/booking/CheckOutDrawer';
import DashboardHeader from '../components/common/DashboardHeader';
import EmptyStateCard from '../components/common/EmptyStateCard';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getDashboardPageShellSx, getDashboardPaperSx } from '../utils/dashboardSurfaceSx';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import { getHotelDateFormat } from '../utils/dateUtils';
import { PERMISSIONS } from '../auth/permissions';
import usePermission from '../auth/usePermission';
import { patchBookingInList } from '../utils/bookingListPatch';
import {
  getBookingOpsControlsLayoutSx,
  getBookingOpsDateFieldSx,
  getBookingOpsDatePickerBoxSx,
  getBookingOpsDateRowSx,
  getBookingOpsFilterToggleSx,
  getBookingOpsFiltersRowSx,
  getBookingOpsGridItemSx,
  getBookingOpsNavButtonSx,
  getBookingOpsTotalLabelSx,
  getBookingOpsTotalRowSx,
  getBookingOpsTotalValueSx,
} from '../utils/bookingOperationsMobileLayout';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const CheckOutPage = () => {
  const normalizeBookingId = (value) => {
    const parsedId = Number(value);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  };
  const navigate = useNavigate();
  const theme = useTheme();
  const { can } = usePermission();
  const canEditBooking = can(PERMISSIONS.BOOKING_MODIFY);
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isMobileLayout = isMobileShellLayout();
  const isDark = currentTheme === 'dark';

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const [selectedDate, setSelectedDate] = useState(moment());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkOutDrawerOpen, setCheckOutDrawerOpen] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'checkout', 'pending'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // 3 rows × 4 cards per row
  const fetchAbortController = useRef(null);
  const debounceTimer = useRef(null);

  // Debounced fetch to prevent multiple rapid calls
  useEffect(() => {
    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Cancel any ongoing fetch
    if (fetchAbortController.current) {
      fetchAbortController.current.abort();
    }

    // Debounce the fetch
    debounceTimer.current = setTimeout(() => {
      setHasFetched(false); // Reset fetch status when date changes
      fetchBookings();
      setCurrentPage(1); // Reset to first page when date changes
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (fetchAbortController.current) {
        fetchAbortController.current.abort();
      }
    };
  }, [selectedDate]);

  const fetchBookings = useCallback(async () => {
    // Create new AbortController for this fetch
    fetchAbortController.current = new AbortController();
    const signal = fetchAbortController.current.signal;

    setLoading(true);
    setError(null);
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const data = await getCheckOutBookings(dateStr, {
        includePendingCheckout: true,
        pendingLookbackDays: 14,
      });
      if (signal.aborted) return;
      setBookings(data.bookings || []);
      setHasFetched(true);
    } catch (err) {
      if (!err.name || err.name !== 'AbortError') {setError('Failed to load checkout bookings. Please try again.');
      }
    } finally {
      if (!fetchAbortController.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [selectedDate]);

  /** Update one card after drawer payment — no full-list loading flash. */
  const refreshBookingCard = useCallback(async (bookingIdArg) => {
    const id = normalizeBookingId(bookingIdArg ?? selectedBookingId);
    if (!id) return;
    try {
      const details = await getBookingDetails(id);
      setBookings((prev) => patchBookingInList(prev, id, details, normalizeBookingId));
    } catch {
      // Keep existing cards; avoid forcing a full-list reload.
    }
  }, [selectedBookingId]);

  const handleDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const handlePrevClick = () => {
    const newDate = selectedDate.clone().subtract(1, 'days');
    setSelectedDate(newDate);
  };

  const handleNextClick = () => {
    const newDate = selectedDate.clone().add(1, 'days');
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(moment());
  };

  const handleBookingClick = (bookingId) => {
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    setSelectedBookingId(safeBookingId);
    setCheckOutDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedBookingId(null);
  };

  const handleCheckOutDrawerClose = () => {
    setCheckOutDrawerOpen(false);
    setSelectedBookingId(null);
  };

  const handleModifyBooking = (bookingId) => {
    if (!canEditBooking) return;
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    setCheckOutDrawerOpen(false);
    setSelectedBookingId(null);
    navigate(`/booking/edit/${safeBookingId}/`);
  };

  const handleCheckOutSuccess = (booking) => {
    // Refresh bookings list after successful checkout
    fetchBookings();
    setCheckOutDrawerOpen(false);
    setSelectedBookingId(null);
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilterType(newFilter);
    }
  };

  // Filter bookings based on selected filter - memoized for performance
  const filteredBookings = useMemo(() => {
    const isPendingCheckoutBooking = (booking) => {
      if (booking?.is_pending_checkout !== undefined && booking?.is_pending_checkout !== null) {
        return !!booking.is_pending_checkout;
      }
      return (
        booking.status === 'checked_in' ||
        booking.status === 'partial_checkin' ||
        booking.status === 'partial_checkout'
      );
    };

    if (filterType === 'all') {
      return bookings;
    } else if (filterType === 'checkout') {
      return bookings.filter(booking => booking.status === 'checked_out');
    } else if (filterType === 'pending') {
      return bookings.filter((booking) => isPendingCheckoutBooking(booking));
    }
    return bookings;
  }, [bookings, filterType]);

  // Pagination logic
  const paginatedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredBookings.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredBookings, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={getDashboardPageShellSx(currentTheme)}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <DashboardHeader
            title="Check-Out"
            forceDark={isMobileShellLayout()}
            breadcrumbs={[
              { label: 'Home', path: '/dashboard/' },
              { label: 'Booking', path: '/booking/' },
              { label: 'Check-Out' }
            ]}
            navLinks={[
              { url: '/booking/list/', label: 'Booking List' },
              { url: '/booking/checkin/', label: 'Check-In' },
            ]}
          />
        </Box>

        {/* Date Navigation and Total Checkout - Same style as check-in */}
        <Paper
          elevation={0}
          sx={getDashboardPaperSx(currentTheme, 'controls')}
        >
          <Box sx={getBookingOpsControlsLayoutSx(isMobileLayout)}>
            <Box sx={getBookingOpsDateRowSx(isMobileLayout)} className="dashboard-date-range">
              <IconButton
                onClick={handlePrevClick}
                className="calendar-nav-button"
                sx={getBookingOpsNavButtonSx(isDark, CUSTOM_BORDER_COLOR)}
              >
                <ChevronLeft />
              </IconButton>
              <Box sx={getBookingOpsDatePickerBoxSx(isMobileLayout)} className="calendar-date-picker">
                <DatePicker
                  value={selectedDate}
                  onChange={handleDateChange}
                  format={getHotelDateFormat()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: getBookingOpsDateFieldSx(isDark, CUSTOM_BORDER_COLOR),
                    },
                  }}
                />
              </Box>
              <IconButton
                onClick={handleNextClick}
                className="calendar-nav-button"
                sx={getBookingOpsNavButtonSx(isDark, CUSTOM_BORDER_COLOR)}
              >
                <ChevronRight />
              </IconButton>
            </Box>

            <Box sx={getBookingOpsFiltersRowSx(isMobileLayout)}>
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={handleFilterChange}
                aria-label="filter type"
                size="small"
                sx={getBookingOpsFilterToggleSx(isMobileLayout, isDark, CUSTOM_BORDER_COLOR)}
              >
                <ToggleButton value="all" aria-label="all">
                  All
                </ToggleButton>
                <ToggleButton value="checkout" aria-label="checkout">
                  Checkout
                </ToggleButton>
                <ToggleButton value="pending" aria-label="pending">
                  Pending
                </ToggleButton>
              </ToggleButtonGroup>

              <Box sx={getBookingOpsTotalRowSx(isMobileLayout, isDark)}>
                <Typography variant="body2" sx={getBookingOpsTotalLabelSx(isDark)}>
                  Total Checkout:
                </Typography>
                <Typography variant="h6" sx={getBookingOpsTotalValueSx(isDark)}>
                  {filteredBookings.length}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Bookings Grid */}
      {!loading && !error && hasFetched && (
        <>
          {filteredBookings.length === 0 ? (
            <EmptyStateCard themeMode={currentTheme}>
              No bookings found for checkout on {selectedDate.format('DD MMM YYYY')}
            </EmptyStateCard>
          ) : (
            <>
              <Grid container spacing={3}>
                {paginatedBookings.map((booking) => {
                  const resolvedBookingId = normalizeBookingId(booking?.id || booking?.booking_id);
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      sx={getBookingOpsGridItemSx()}
                      key={resolvedBookingId || `${booking?.code || 'booking'}_${booking?.check_in_date || ''}_${booking?.check_out_date || ''}`}
                    >
                      <BookingOpsBookingCard
                        booking={booking}
                        isDark={isDark}
                        accent={CUSTOM_BORDER_COLOR}
                        isMobile={isMobileLayout}
                        onClick={() => handleBookingClick(resolvedBookingId)}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: isDark ? '#ddd' : '#333',
                        '&.Mui-selected': {
                          backgroundColor: CUSTOM_BORDER_COLOR,
                          color: '#000',
                          '&:hover': {
                            backgroundColor: CUSTOM_BORDER_COLOR,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Check-Out Drawer */}
      <CheckOutDrawer
        bookingId={selectedBookingId}
        open={Boolean(checkOutDrawerOpen && selectedBookingId)}
        onClose={handleCheckOutDrawerClose}
        onCheckOutSuccess={handleCheckOutSuccess}
        onModifyBooking={canEditBooking ? handleModifyBooking : undefined}
        onBookingUpdated={refreshBookingCard}
      />

      {/* Booking Details Drawer (for reference, can be opened via other means) */}
      <BookingDetailsDrawer
        bookingId={selectedBookingId}
        open={Boolean(drawerOpen && selectedBookingId)}
        onClose={handleDrawerClose}
        onStatusUpdate={refreshBookingCard}
      />
    </Box>
    </LocalizationProvider>
  );
};

export default CheckOutPage;

